import { Button, Dialog, DialogActions, DialogContent, Slider } from '@mui/material';
import { useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { I18nNamespace } from '~/common/types';

import { CropAreaContainer } from './UserProfileCropDialog.styled';
import { getCroppedPicture } from './UserProfileCropDialog.util';

interface UserProfileCropDialogProps {
  pictureBlobSrc: string;
  onPictureCropped: (croppedPicture?: Union.Nullable<Blob>) => void;
  open: boolean;
  setOpen: (isOpen: boolean) => void;
}

export const UserProfileCropDialog = ({
  pictureBlobSrc,
  onPictureCropped,
  open,
  setOpen,
}: UserProfileCropDialogProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [zoom, setZoom] = useState(1);
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileDetailsWidget.crop' });

  const handleApply = async () => {
    if (croppedAreaPixels) {
      const croppedPicture = await getCroppedPicture(pictureBlobSrc, croppedAreaPixels);
      if (croppedPicture) {
        onPictureCropped(croppedPicture);
      }
    }
    setOpen(false);
  };

  const handleClose = () => {
    if (pictureBlobSrc) {
      URL.revokeObjectURL(pictureBlobSrc);
    }
    onPictureCropped(null);
    setOpen(false);
  };

  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogContent>
        <CropAreaContainer>
          <Cropper
            image={pictureBlobSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </CropAreaContainer>
        <Slider
          value={zoom}
          min={1}
          max={3}
          step={0.1}
          aria-labelledby="Zoom"
          onChange={(_e, zoom) => setZoom(zoom as number)}
          sx={{ padding: 0 }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={handleApply} color="primary" type="submit">
          {t('apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
